"use client";

import { motion, useReducedMotion } from "framer-motion";
import {
  Archive,
  ArrowRight,
  Blocks,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  Facebook,
  Flag,
  Forward,
  Instagram,
  Mail,
  Moon,
  Reply,
  Smartphone,
  Sparkles,
  SquarePen,
  Trash2,
  Youtube,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const EASE = [0.16, 1, 0.3, 1] as const;

const CAPABILITIES = [
  { icon: Blocks, label: "Drag & drop" },
  { icon: Smartphone, label: "Mobile-first" },
  { icon: Moon, label: "Dark mode ready" },
  { icon: Sparkles, label: "On-brand by default" },
];

/* ------------------------------------------------------------------ */
/* Hero copy                                                           */
/* ------------------------------------------------------------------ */

function HeroCopy() {
  const reduceMotion = useReducedMotion();
  const item = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
  };
  const container = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
  };
  const containerProps = reduceMotion
    ? {}
    : { initial: "hidden" as const, animate: "visible" as const, variants: container };
  const iv = reduceMotion ? undefined : item;

  return (
    <motion.div
      className="flex max-w-xl flex-col items-center text-center xl:items-start xl:text-left"
      {...containerProps}
    >
      <motion.span
        variants={iv}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground"
      >
        <Mail className="size-4" />
        Email Builder
      </motion.span>

      <motion.h1
        variants={iv}
        className="mt-4 text-balance text-4xl font-bold leading-[1.1] tracking-tight text-foreground sm:text-[2.75rem] lg:text-5xl"
      >
        Email that looks like you hired a designer
      </motion.h1>

      <motion.p
        variants={iv}
        className="mt-6 max-w-lg text-balance leading-relaxed text-muted-foreground sm:text-lg"
      >
        Drag in a few blocks and send an on-brand email that looks right on every phone and in every
        inbox. No designer, no broken layouts, no fuss.
      </motion.p>

      <motion.div variants={iv} className="mt-8 w-full sm:w-auto">
        <Button asChild size="xl" className="group w-full sm:w-auto">
          <Link href="/book-a-demo">
            Book a demo
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </Button>
      </motion.div>

      <motion.div
        variants={iv}
        className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 xl:justify-start"
      >
        {CAPABILITIES.map(({ icon: Icon, label }) => (
          <span
            key={label}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground"
          >
            <Icon className="size-3.5" />
            {label}
          </span>
        ))}
      </motion.div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Apple Mail message view — the HTML email as the recipient sees it.   */
/* Fully theme-aware: light shows the standard email; dark shows Mail's */
/* dark chrome plus the email's dark-mode variant.                      */
/* ------------------------------------------------------------------ */

const FITT_LOGO = "/images/website/fitt-pilates.jpg";
const FITT_LOGO_WIDE = "/images/website/fitt-pilates-logo-wide.webp";
const FITT_LOGO_LIGHT_WIDE = "/images/website/fitt-pilates-logo-light-wide.png";
const FITT_STUDIO = "/images/website/fitt-sudio.jpg";

/**
 * The HTML email itself, as the recipient sees it. Mirrors how Helios renders an
 * email in-app (left-aligned logo header, inset image, heading/text/button
 * blocks, footer) and is shared by the phone and desktop mocks. The body is
 * theme-aware to show off dark-mode support.
 */
function EmailBody({ heroClassName, compact }: { heroClassName?: string; compact?: boolean }) {
  return (
    <div className="bg-white font-sans text-[#374151] dark:bg-background dark:text-[#c7c7cc]">
      <div className={cn("mx-auto w-full", compact ? "max-w-[520px]" : "max-w-[560px]")}>
        {/* Logo header — brand logo (theme-swapped) with social icons */}
        <div
          className={cn(
            "flex items-center justify-between gap-4",
            compact ? "px-6 pt-4" : "px-7 pt-6",
          )}
        >
          {/* Light-mode logo (dark text) */}
          <Image
            src={FITT_LOGO_LIGHT_WIDE}
            alt="Fitt Pilates"
            width={1500}
            height={471}
            className="h-8 w-auto object-contain dark:hidden"
          />
          {/* Dark-mode logo (white text) */}
          <Image
            src={FITT_LOGO_WIDE}
            alt="Fitt Pilates"
            width={1500}
            height={471}
            className="hidden h-8 w-auto object-contain dark:block"
          />
          {/* Social icons — display only (no link in this preview) */}
          <div className="flex items-center gap-3.5 text-[#9ca3af] dark:text-[#8e8e93]">
            <Instagram className="size-[18px]" />
            <Facebook className="size-[18px]" />
            <Youtube className="size-[18px]" />
          </div>
        </div>

        {/* Hero image — inset within the body padding, as the app renders image blocks */}
        <div className={cn(compact ? "px-6 pt-3.5" : "px-7 pt-5")}>
          <Image
            src={FITT_STUDIO}
            alt="Inside the new Fitt Pilates studio"
            width={1200}
            height={800}
            className={cn("w-full rounded-[6px] object-cover", heroClassName ?? "h-[160px]")}
          />
        </div>

        {/* Content */}
        <div className={cn(compact ? "px-6 pb-5 pt-3.5" : "px-7 pb-7 pt-5")}>
          <h2 className="text-[20px] font-bold leading-tight text-[#1f2937] dark:text-white">
            We just opened on South Congress
          </h2>
          <p className="mt-2 text-[14px] leading-[1.55] text-[#374151] dark:text-[#c7c7cc]">
            Join us Saturday, June 28 for free classes all weekend, plus founding-member pricing for
            the first 100 to sign up.
          </p>
          <div className="mt-4">
            <span className="inline-flex items-center rounded-md bg-[#007AFF] px-7 py-2.5 text-[14px] font-semibold text-white">
              Reserve your free class
            </span>
          </div>
        </div>

        {compact ? null : (
          <>
            {/* Footer — mirrors the app's footer block */}
            <div className="mx-7 h-px bg-[#e5e7eb] dark:bg-[#38383a]" />
            <div className="px-7 py-5">
              <p className="text-[13px] font-bold text-[#374151] dark:text-[#e5e5ea]">
                Fitt Pilates
              </p>
              <p className="mt-1 text-[12px] text-[#6b7280] dark:text-[#98989d]">
                1500 South Congress Ave, Austin, TX
              </p>
              <p className="mt-3 text-[11px] leading-relaxed text-[#9ca3af] dark:text-[#8e8e93]">
                You are receiving this email because you opted in to updates from Fitt Pilates.
              </p>
              <p className="mt-1 text-[11px] text-[#9ca3af] dark:text-[#8e8e93]">
                <span className="underline">Unsubscribe</span>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** iOS Mail reading view — shown on phones / narrow viewports. */
function PhoneMail() {
  return (
    <div className="w-[340px] overflow-hidden rounded-[28px] bg-white shadow-2xl shadow-black/25 ring-1 ring-black/10 sm:w-[380px] dark:bg-black dark:shadow-black/60 dark:ring-white/10">
      {/* Mail nav bar */}
      <div className="flex items-center justify-between border-b border-[#e5e5ea] px-3.5 py-2.5 dark:border-[#38383a]">
        <span className="inline-flex items-center gap-0.5 text-[15px] font-normal text-[#007aff] dark:text-[#0a84ff]">
          <ChevronLeft className="size-[18px]" strokeWidth={2.5} />
          Inbox
        </span>
        <span className="flex items-center gap-5 text-[#007aff] dark:text-[#0a84ff]">
          <ChevronUp className="size-[18px]" strokeWidth={2.5} />
          <ChevronDown className="size-[18px] opacity-30" strokeWidth={2.5} />
        </span>
      </div>

      {/* Sender + subject */}
      <div className="px-4 pt-3.5">
        <div className="flex items-start gap-3">
          <Image
            src={FITT_LOGO}
            alt="Fitt Pilates"
            width={80}
            height={80}
            className="size-10 shrink-0 rounded-full object-cover"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <p className="truncate text-[15px] font-semibold text-[#1f2937] dark:text-white">
                Fitt Pilates
              </p>
              <span className="shrink-0 text-[13px] text-[#8e8e93]">9:41 AM</span>
            </div>
            <div className="mt-0.5 flex items-center justify-between gap-2">
              <p className="truncate text-[13px] text-[#8e8e93]">
                To: Maya
                <ChevronDown className="ml-0.5 inline size-3 align-middle" strokeWidth={2.5} />
              </p>
              <Reply className="size-4 shrink-0 text-[#8e8e93]" />
            </div>
          </div>
        </div>
        <p className="mt-3 text-[17px] font-semibold leading-snug text-[#1f2937] dark:text-white">
          You're invited to our South Congress grand opening
        </p>
      </div>

      <div className="mt-3 h-px bg-[#e5e5ea] dark:bg-[#38383a]" />

      <EmailBody />

      {/* Mail action toolbar */}
      <div className="flex items-center justify-between border-t border-[#e5e5ea] px-7 py-3 text-[#007aff] dark:border-[#38383a] dark:text-[#0a84ff]">
        <Flag className="size-[18px]" />
        <Trash2 className="size-[18px]" />
        <Reply className="size-[18px]" />
        <Forward className="size-[18px]" />
        <SquarePen className="size-[18px]" />
      </div>
    </div>
  );
}

/** Minimal desktop mail window — landscape, the email centered on a reading canvas. */
function DesktopMail({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "flex h-[486px] w-full flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-xl shadow-black/5 dark:shadow-black/40",
        className,
      )}
    >
      {/* Window title bar */}
      <div className="flex items-center gap-3 border-b border-border bg-muted/40 px-4 py-2.5">
        <span className="flex items-center gap-1.5">
          <span className="size-3 rounded-full bg-[#ff5f57]" />
          <span className="size-3 rounded-full bg-[#febc2e]" />
          <span className="size-3 rounded-full bg-[#28c840]" />
        </span>
        <span className="ml-auto flex items-center gap-4 text-muted-foreground">
          <Archive className="size-4" />
          <Trash2 className="size-4" />
          <Reply className="size-4" />
          <Forward className="size-4" />
        </span>
      </div>

      {/* Message header — subject + from/to so it's unmistakably an email */}
      <div className="border-b border-border px-6 py-3.5">
        <p className="text-[15px] font-semibold leading-snug text-foreground">
          You're invited to our South Congress grand opening
        </p>
        <div className="mt-2.5 flex items-center gap-3">
          <Image
            src={FITT_LOGO}
            alt="Fitt Pilates"
            width={80}
            height={80}
            className="size-8 shrink-0 rounded-full object-cover"
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] text-foreground">
              <span className="font-semibold">Fitt Pilates</span>{" "}
              <span className="text-muted-foreground">&lt;hello@fittpilates.com&gt;</span>
            </p>
            <p className="truncate text-[12px] text-muted-foreground">To: Maya Rivera</p>
          </div>
          <span className="shrink-0 text-[12px] text-muted-foreground">9:41 AM</span>
        </div>
      </div>

      {/* Reading pane — the email fills the container, full bleed (no boxing) */}
      <div className="min-h-0 flex-1 overflow-hidden bg-white dark:bg-background">
        <EmailBody compact heroClassName="h-[144px]" />
      </div>
    </div>
  );
}

function Stage() {
  const reduceMotion = useReducedMotion();
  const cardAnim = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, x: 24, scale: 0.98 },
        animate: { opacity: 1, x: 0, scale: 1 },
        transition: { duration: 0.8, ease: EASE, delay: 0.45 },
      };

  return (
    <motion.div {...cardAnim} className="relative w-full">
      {/* Desktop: landscape mail client */}
      <DesktopMail className="hidden xl:flex" />
      {/* Mobile / tablet: iOS Mail phone view */}
      <div className="mx-auto w-[340px] sm:w-[380px] xl:hidden">
        <PhoneMail />
      </div>
    </motion.div>
  );
}

export function EmailBuilderHero() {
  return (
    <section className="relative overflow-x-clip border-b border-border bg-background pb-16 pt-28 sm:pb-20 lg:pt-36">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-4 lg:px-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,660px)] xl:gap-12">
        <HeroCopy />
        <Stage />
      </div>
    </section>
  );
}
